import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import {
    AddField,
    ColFileStructure,
    ConfiguratorDatastoreService,
    ConfiguratorEtlService,
    ConfiguratorTableService,
    ConvertTypeTransformConf,
    EtlDesignDTO,
    FieldTable,
    SqlLoadConf,
    SqlProcessingLoadConf,
    StringTransformConf,
    Table,
} from "@tj/tj-dmp-lib-api";
import {
    ConfirmationService,
    MessageService,
    SelectItem,
    SelectItemGroup,
} from "primeng/api";
import { EtlUpdateDesignService } from "./etl-update-design.service";
import { EtlFileSource } from "./etl-file-source.interface";
import {
    ITargetFieldDestination,
    TargetFieldDestination,
} from "./target-field-destination.interface";
import { EtlFileSourceInterface } from "./etl-file-source.interface";
import { AbstractEtlUpdateComponent } from "../abstract-etl-update.component";
import { Router } from "@angular/router";
import { AccountService } from "../../../../auth/account.service";
import { isAdminOfTenant } from "../../../../auth/admin-levels";
import { switchMap, tap } from "rxjs/operators";
import { of } from "rxjs";

@Component({
    // tslint:disable-next-line:component-selector
    selector: "dmp-etl-update-design2",
    templateUrl: "./etl-update-design2.component.html",
    styleUrls: ["./etl-update-design2.component.scss"],
    standalone: false
})
export class EtlUpdateDesign2Component
    extends AbstractEtlUpdateComponent
    implements OnInit
{
    constructor(
        protected tableResourceService: ConfiguratorTableService,
        protected fb: UntypedFormBuilder,
        protected configuratorEtlService: ConfiguratorEtlService,
        protected ref: ChangeDetectorRef,
        protected etlUpdateDesignService: EtlUpdateDesignService,
        protected confirmationService: ConfirmationService,
        protected translate: TranslateService,
        protected router: Router,
        protected messageService: MessageService,
        protected accountService: AccountService,
        protected datastoreService: ConfiguratorDatastoreService
    ) {
        super(router, confirmationService, translate);
    }

    /** Tous les champs de destination possibles, par table (base des listes de choix). */
    groupedFieldsByTableOrigin: SelectItemGroup[] = [];
    /** Colonnes du fichier analysé. */
    listeCodesOrigin: SelectItem[];

    tables: Table[];
    etl: EtlDesignDTO;
    adminOnlyFields: any[] = [];

    /** Champ en cours de paramétrage */
    fields: TargetFieldDestination[] = null;
    /** Champ STATIC en cours de paramétrage */
    fieldStatic: TargetFieldDestination = null;
    /**
     * Édition d'un mapping existant : copie passée à la modale → original dans `fieldsTable`. Les
     * modales modifient en direct ce qu'on leur passe (ngModel) ; sans copie, « Annuler » laissait les
     * modifications en place (docs/ui-design-guidelines.md §1.1).
     */
    private editOriginals = new Map<TargetFieldDestination, TargetFieldDestination>();

    /** Tous les mappings de l'import : colonnes du fichier et valeurs fixes. Source de vérité. */
    fieldsTable: TargetFieldDestination[] = [];

    /*
     * Écran : une ligne par colonne du fichier, avec ses destinations (docs/ui-design-guidelines.md
     * §12.2). Les vues ci-dessous sont recalculées depuis `fieldsTable` par `refreshMappings()`.
     */
    sourceRows: SourceRow[] = [];
    visibleRows: SourceRow[] = [];
    staticFields: TargetFieldDestination[] = [];
    mappedCount = 0;
    /** Destinations proposées dans la ligne d'une colonne non mappée : hors tables à lignes multiples,
     *  hors destinations déjà prises. */
    destinationOptions: SelectItemGroup[] = [];
    /** Champs obligatoires non mappés, affichés en bandeau au-dessus du tableau. */
    missingRequired: RequiredField[] = [];

    columnSearch = "";
    rowFilter: RowFilter = "all";
    rowFilterOptions: SelectItem[] = [];
    /** Colonnes cochées, pour les mappings qui portent sur plusieurs colonnes (lignes multiples, UTM). */
    selectedRows: SourceRow[] = [];

    isValid = false;

    /*
     * Contexte de la modale de mapping (`effectiveOnlyTable`, `onlyUtmFields`) pendant un mapping de
     * plusieurs colonnes. Remis à zéro à chaque fermeture de la modale.
     */
    modeGroupe = false;
    tableGroupage: string;
    displayTableGroupage = false;
    modeUtmFilter = false;
    displayUtmFilterConfig = false;
    private utmFilterConfigClosedByAction = false;
    utmFilterTable: string = null;
    /** Choix en cours dans la modale UTM : la table appliquée (`utmFilterTable`) ne change qu'à la
     *  validation. */
    utmFilterDraft: string = null;

    /** Dernière table à lignes multiples utilisée : « Ligne n° N de … » enchaîne la ligne suivante
     *  sans rechoisir la table. */
    multiRowsTable: string = null;

    /* scripts */
    showEditScript = false;
    scriptPostMapping: string;

    contactType: SqlProcessingLoadConf.ContactTypeEnum;

    isAuthorisedAdmin: boolean = false;

    ngOnInit(): void {
        if (this.id) {
            this.accountService.fetch().pipe(
                tap(account => {
                    this.isAuthorisedAdmin = isAdminOfTenant(account, this.tenant);
                }),
                switchMap(() => {
                    // un utilisateur de l'affilie par défaut peut voir les champs adminOnly
                    return !this.isAuthorisedAdmin ? this.datastoreService.getDatastoreUsingGET(this.tenant, true) : of(null);
                }),
                tap(datastoreDto => {
                    if (datastoreDto?.defaultPartner === this.partner) {
                        this.isAuthorisedAdmin = true;
                    }
                }),
                switchMap(() => this.configuratorEtlService.getDesignUsingGET(this.tenant, this.id))
            ).subscribe((etlDesign) => {
                this.completChange.emit(etlDesign.complet);
                    const sqlLoadConf = etlDesign.load as SqlProcessingLoadConf;
                    this.contactType = sqlLoadConf.contactType;
                this.loadTableFields(
                    etlDesign,
                    etlDesign.load as SqlProcessingLoadConf
                );
                this.loadFileFields(etlDesign.cols);
                this.scriptPostMapping = etlDesign.scriptPostMapping;
            });
        }
    }

    /**
     * Champs obligatoires non mappés. Même règle que l'ancien message d'erreur : un champ obligatoire
     * est couvert dès que son code est mappé ; la validité de l'étape n'a pas changé.
     */
    private updateRequiredFields(): void {
        if (!this.tables) {
            this.missingRequired = [];
            this.isValid = false;
            return;
        }
        const mapped = new Set(this.fieldsTable.map((ft) => ft.fieldDest));
        const missing: RequiredField[] = [];
        this.tables.forEach((table) => {
            table.fields
                .filter((f) => this.shouldIncludeInRequiredFields(f) && !mapped.has(f.code))
                .forEach((f) => missing.push({
                    tableDest: table.code,
                    field: f,
                    multiRows: this.isMultiRowsTable(table.code),
                }));
        });
        // "ID de l'affilié" affiché en premier dans la liste des champs requis
        missing.sort((a, b) =>
            (b.field.categoryField === "AFFILIATION" ? 1 : 0) -
            (a.field.categoryField === "AFFILIATION" ? 1 : 0)
        );
        this.missingRequired = missing;
        this.isValid = missing.length === 0;
    }

    loadFileFields(cols: ColFileStructure[]) {
        this.listeCodesOrigin = cols?.map((col) => {
            return {
                label: `${col.code}`,
                disabled: false,
                value: new EtlFileSource(col),
            } as SelectItem;
        });
        this.refreshMappings();
    }

    updateValue(etl: EtlDesignDTO) {
        this.etl = etl;
        this.fieldsTable = [];

        this.etl.addFields?.forEach((field) => {
            const metaField = this.tables
                .find((t) => t.code === field.tableDest)
                .fields.find((f) => f.code === field.fieldDest);
            
            if (this.isAuthorisedAdmin || !metaField?.adminOnly) {
                this.fieldsTable.push(
                    TargetFieldDestination.__setAddField(field, metaField)
                );
            }
        });

        this.etl.load?.loads?.forEach((load) => {
            const field = load as SqlLoadConf;
            const metaField = this.tables
                .find((t) => t.code === field.tableDest)
                .fields.find((f) => f.code === field.fieldDest);

            if (this.isAuthorisedAdmin || !metaField?.adminOnly) {
                // All transforms for this source field
                const transformsBySrc = this.etl.transforms?.filter(
                    (t) => t.fieldSrc === field.fieldSrc
                ) ?? [];

                // Prefer transforms that also match the destination (field + table)
                const transformsExact = transformsBySrc.filter(
                    (t) =>
                        t.fieldDest === field.fieldDest &&
                        t.tableDest === field.tableDest
                );

                const transforms =
                    transformsExact.length > 0 ? transformsExact : transformsBySrc;

                const exampleValues = this.etl.cols.find(
                    (c) => c.code === field.fieldSrc
                ).exampleValues;

                this.fieldsTable.push(
                    TargetFieldDestination.__setSqlConf(
                        field,
                        metaField,
                        transforms,
                        exampleValues
                    )
                );
            }
        });
    }

    loadTableFields(
        etlSetup: EtlDesignDTO,
        sqlLoadConf: SqlProcessingLoadConf
    ): void {
        const destTable : Array<string> = sqlLoadConf.destTable;
        this.tableResourceService
            .getTablesUsingGET(this.tenant, destTable)
            .subscribe((tables) => {
                this.tables = destTable
                    .map(tableName =>
                    tables.find(table => table.code === tableName)
                    ) as Table[];
                this.extractAdminOnlyFields();
                this.updateValue(etlSetup);

                this.groupedFieldsByTableOrigin = this.tables.map((t) => {
                    const fields = t.fields
                        .filter((f) => this.shouldIncludeField(f))
                        .map((f) => {
                            // "ID de l'affilié" (catégorie AFFILIATION) toujours affiché comme requis
                            const isRequired = f.notNull || f.categoryField === "AFFILIATION";
                            return {
                                label: `${f.label || f.code} - ${this.getCatOrType(f)}`,
                                value: { fieldDest: f.code, tableDest: t.code },
                                styleClass: isRequired
                                    ? "required"
                                    : "not-required",
                                disabled: false,
                            } as SelectItem;
                        });
                    return {
                        label: `Table ${t.code}`,
                        value: t.code,
                        items: fields,
                    };
                });
                this.refreshMappings();
            });
    }

    /**
     * @deprecated
     * @private
     */
    private updateTargetField() {
        //
        const listDeChampParCodeSource: any = this.etl.load.loads.reduce(
            (r, a) => {
                r[a.fieldSrc] = r[a.fieldSrc] || [];
                r[a.fieldSrc].push(a);
                return r;
            },
            Object.create(null)
        );
        for (const key in listDeChampParCodeSource) {
            if (
                Object.prototype.hasOwnProperty.call(
                    listDeChampParCodeSource,
                    key
                )
            ) {
                const elements: any[] = listDeChampParCodeSource[key];
                const element1 = elements[0];
                if (element1.typeObject === "sql") {
                    const slqF = element1 as SqlLoadConf;
                    const col = this.etl.cols.find(
                        (c) =>
                            c.code.toLowerCase() === slqF.fieldSrc.toLowerCase()
                    );
                    const exampleValues = col ? col.exampleValues : [];
                    const targetField = {
                        type: "target",
                        code: slqF.fieldSrc,
                        name: slqF.fieldSrc,
                        exampleValues,
                        destinations: [],
                    };
                    // this.targetFields.push(targetField);
                    elements
                        .map((item) => item as SqlLoadConf)
                        .forEach((elementSlqF) => {
                            const table = this.tables.find(
                                (t) => t.code === elementSlqF.tableDest
                            );
                            const field = table.fields.find(
                                (f) => f.code === elementSlqF.fieldDest
                            );
                            //
                            const destination: ITargetFieldDestination = {
                                code: elementSlqF.fieldSrc,
                                name: elementSlqF.fieldSrc,
                                fieldDest: elementSlqF.fieldDest,
                                tableDest: elementSlqF.tableDest,
                                meta: field,
                                replace: false,
                                replaceRegex: null,
                                replaceValue: null,
                                replaceAll: null,
                                pattern: null,
                                origin: "source",
                            };
                            //
                            this.etl.transforms
                                .filter(
                                    (t) =>
                                        t.fieldSrc === slqF.fieldSrc &&
                                        (t.fieldDest == null ||
                                            t.fieldDest ===
                                                elementSlqF.fieldDest)
                                )
                                .forEach((t) => {
                                    if (
                                        t.typeTransform ===
                                        "ConvertTypeTransformConf"
                                    ) {
                                        const convertT =
                                            t as ConvertTypeTransformConf;
                                        if (convertT.pattern) {
                                            destination.pattern =
                                                convertT.pattern;
                                        }
                                    } else if (
                                        t.typeTransform ===
                                        "ReplaceHeaderTransformConf"
                                    ) {
                                        // Not yet
                                    } else if (
                                        t.typeTransform ===
                                        "TranslatorTransformConf"
                                    ) {
                                        // Net yet
                                    } else if (
                                        t.typeTransform ===
                                        "StringTransformConf"
                                    ) {
                                        const stringT =
                                            t as StringTransformConf;
                                        if (stringT.regex) {
                                            destination.replace = true;
                                            destination.replaceRegex =
                                                stringT.regex;
                                            destination.replaceValue =
                                                stringT.replacement;
                                            destination.replaceAll =
                                                stringT.all;
                                        }
                                    }
                                });
                            //
                            targetField.destinations.push(destination);
                        });
                    // this.onModifPopParam(targetField);
                }
            }
            // // sort
            // this.targetFields = this.targetFields.sort((a, b) =>
            //   a.code > b.code ? 1 : -1
            // );
            // // clear source
            // this.targetFields.forEach((f) => {
            //   const colIndex = this.sourceFields.findIndex((c) => {
            //     return c.code.toLowerCase() == f.code.toLowerCase();
            //   });
            //   if (colIndex > -1) {
            //     this.sourceFields.splice(colIndex, 1);
            //   }
            // });
        }
    }


    private getCatOrType(f: FieldTable) {
        let res = this.etlUpdateDesignService.getCategory(f);
        if (res == null) {
            res = this.etlUpdateDesignService.getType(f);
        }
        return res;
    }

    /** Table qui accepte plusieurs lignes par ligne du fichier (les passagers d'une commande). */
    isMultiRowsTable(tableCode: string): boolean {
        const table = (this.tables || []).find((t) => t.code === tableCode);
        return (table?.tableDependencies || []).some((td) => td.cardinalie === "0n01");
    }

    tableLabel(tableCode: string): string {
        const table = (this.tables || []).find((t) => t.code === tableCode);
        return table?.label || tableCode;
    }

    /**
     * Recalcule l'écran depuis `fieldsTable` : lignes de colonnes, valeurs fixes, destinations encore
     * libres, champs obligatoires manquants, filtres. Appelé après chaque changement de mapping.
     */
    refreshMappings(): void {
        const cols = this.listeCodesOrigin || [];
        const byCode = new Map<string, TargetFieldDestination[]>();
        this.fieldsTable
            .filter((f) => f.origin !== "static")
            .forEach((f) => {
                const list = byCode.get(f.code) || [];
                list.push(f);
                byCode.set(f.code, list);
            });

        const rows: SourceRow[] = cols.map((item) => ({
            code: item.label,
            source: item.value,
            mappings: byCode.get(item.label) || [],
            missing: false,
        }));
        // Mapping d'une colonne qui n'est plus dans le fichier analysé : montré plutôt que caché, il
        // est toujours enregistré.
        byCode.forEach((mappings, code) => {
            if (!cols.some((c) => c.label === code)) {
                rows.push({code, source: {code, name: code, exampleValues: null}, mappings, missing: true});
            }
        });
        rows.forEach((row) => row.mappings.sort((a, b) =>
            (a.groupe || 0) - (b.groupe || 0) || (a.tableDest || "").localeCompare(b.tableDest || "")
        ));

        this.sourceRows = rows;
        this.staticFields = this.fieldsTable.filter((f) => f.origin === "static");
        this.mappedCount = rows.filter((r) => !r.missing && r.mappings.length > 0).length;

        const used = new Set(this.fieldsTable.map((f) => `${f.tableDest}::${f.fieldDest}`));
        this.destinationOptions = this.groupedFieldsByTableOrigin
            .filter((g) => !this.isMultiRowsTable(g.value))
            .map((g) => ({
                ...g,
                items: g.items.filter((i) => !used.has(`${i.value.tableDest}::${i.value.fieldDest}`)),
            }))
            .filter((g) => g.items.length > 0);

        this.updateRequiredFields();
        this.applyRowFilter();
    }

    applyRowFilter(): void {
        const search = (this.columnSearch || "").trim().toLowerCase();
        this.visibleRows = this.sourceRows.filter((row) =>
            (!search || this.rowMatches(row, search)) && ROW_FILTERS[this.rowFilter].matches(row)
        );
        this.rowFilterOptions = (Object.keys(ROW_FILTERS) as RowFilter[]).map((value) => ({
            label: `${this.translate.instant(ROW_FILTERS[value].label)} (${this.sourceRows.filter(ROW_FILTERS[value].matches).length})`,
            value,
        }));
    }

    /**
     * Recherche sur la colonne du fichier, mais aussi sur ses destinations (code, libellé, table) : on
     * cherche aussi bien « où va number » que « quelle colonne alimente l'email ».
     */
    private rowMatches(row: SourceRow, search: string): boolean {
        return row.code.toLowerCase().includes(search) || row.mappings.some((m) =>
            [m.fieldDest, m.meta?.label, m.tableDest].some((v) => (v || "").toLowerCase().includes(search))
        );
    }

    /**
     * Mapping choisi dans la ligne d'une colonne. Le cas courant est enregistré tel quel, par le même
     * chemin que la validation de la modale ; une destination qui demande un paramétrage (date à
     * mettre au format, champ UTM et ses règles) ouvre la modale, préremplie.
     */
    mapInline(row: SourceRow, destination: { tableDest: string; fieldDest: string }): void {
        if (!destination) {
            return;
        }
        const target = new TargetFieldDestination();
        target.updateWithDestinationField(destination.tableDest, destination.fieldDest);
        target.updateWithSourceField(row.source, 0);
        target.meta = (this.tables || [])
            .find((t) => t.code === destination.tableDest)
            ?.fields.find((f) => f.code === destination.fieldDest);
        target.replace = false;

        if (target.utmMatching || this.etlUpdateDesignService.isDateTimeType(target)) {
            this.fields = [target];
            return;
        }
        this.onModifPopUpField([target]);
        this.refreshMappings();
    }

    /** Destination supplémentaire pour une colonne déjà mappée : la modale, colonne préremplie. */
    addDestination(row: SourceRow): void {
        const target = new TargetFieldDestination();
        target.updateWithSourceField(row.source, 0);
        this.fields = [target];
    }

    /** Depuis le bandeau des champs obligatoires : la modale, destination préremplie, colonne à choisir. */
    mapRequired(required: RequiredField): void {
        if (required.multiRows) {
            return;
        }
        const target = new TargetFieldDestination();
        target.updateWithDestinationField(required.tableDest, required.field.code);
        this.fields = [target];
    }

    editMapping(mapping: TargetFieldDestination): void {
        if (mapping.groupe > 0) {
            this.doEditGroup(mapping.tableDest, mapping.groupe);
        } else {
            this.doEdit(mapping);
        }
    }

    removeMapping(mapping: TargetFieldDestination, event: Event): void {
        this.confirmationService.confirm({
            target: event.target,
            message: this.translate.instant("common.confirm"),
            acceptLabel: this.translate.instant("common.True"),
            rejectLabel: this.translate.instant("common.False"),
            icon: "fas fa-exclamation-triangle",
            accept: () => {
                // Par identité : un même champ en base peut apparaître dans plusieurs lignes d'une table
                // à lignes multiples, seul celui-ci est retiré.
                this.fieldsTable = this.fieldsTable.filter((f) => f !== mapping);
                this.refreshMappings();
            },
        });
    }

    public onClosePopParam(event): void {
        // Annulation (ou fin de validation) : les copies de travail sont abandonnées.
        this.editOriginals.clear();
        this.fields = null;
        this.fieldStatic = null;
        this.modeUtmFilter = false;
        this.utmFilterTable = null;
        this.displayUtmFilterConfig = false;
        this.modeGroupe = false;
        this.tableGroupage = null;
        this.refreshMappings();
    }

    public onAddStaticField() {
        const targetField = new TargetFieldDestination();
        targetField.newStaticField();
        this.fieldStatic = targetField;
    }

    public onEditScripts() {
        this.showEditScript = true;
    }

    onDeletePopupField(fieldInputs: TargetFieldDestination[]) {
        // Copie retirée dans la modale : c'est son original qui part, même si la destination de la copie
        // avait été changée avant.
        fieldInputs.forEach((fieldInput) => {
            const original = this.editOriginals.get(fieldInput);
            if (original) {
                this.fieldsTable = this.fieldsTable.filter((f) => f !== original);
            }
        });
        fieldInputs.forEach((fieldInput) => {
            this.fieldsTable = this.fieldsTable.filter(
                (field) =>
                    !(
                        fieldInput.fieldDest === field.fieldDest &&
                        fieldInput.tableDest === field.tableDest &&
                        fieldInput.groupe === field.groupe
                    )
            );
        });
    }

    /**
     * lors de la validation de la popup de paramétrage du champs source
     * @param fieldInputs données en entrée
     */
    public onModifPopUpField(fieldInputs: TargetFieldDestination[]): void {
        // Édition validée : la copie est reportée sur l'original. Son identité est gardée, si bien que la
        // recherche ci-dessous le retrouve comme avant, même quand la destination a changé dans la modale.
        fieldInputs.forEach((fieldInput) => {
            const original = this.editOriginals.get(fieldInput);
            if (original) {
                Object.assign(original, fieldInput);
            }
        });
        fieldInputs.forEach((fieldInput) => {
            if (fieldInput.utmMatching && this.isUtmFieldCode(fieldInput.fieldDest)) {
                const duplicate = this.fieldsTable.find(
                    (field) =>
                        field.tableDest === fieldInput.tableDest &&
                        field.fieldDest === fieldInput.fieldDest &&
                        !(
                            field.fieldDest === fieldInput.fieldDest &&
                            field.tableDest === fieldInput.tableDest &&
                            field.groupe === fieldInput.groupe
                        )
                );
                if (duplicate) {
                    this.messageService.add({
                        severity: "warn",
                        summary: "Mapping UTM ignoré",
                        detail: `Le champ ${fieldInput.tableDest}.${fieldInput.fieldDest} est déjà mappé et doit rester unique.`,
                        key: "popin",
                    });
                    return;
                }
            }
            const index = this.fieldsTable.findIndex(
                (field) =>
                    fieldInput.fieldDest === field.fieldDest &&
                    fieldInput.tableDest === field.tableDest &&
                    fieldInput.groupe === field.groupe
            );

            const fieldInoutCopy = JSON.parse(JSON.stringify(fieldInput));
            if (index === -1) {
                this.fieldsTable.push(fieldInoutCopy);
            } else {
                this.fieldsTable[index] = fieldInoutCopy;
            }
        });

        // Colonnes cochées puis mappées (lignes multiples, UTM) : la sélection a servi, on la vide. Une
        // annulation la garde, pour pouvoir recommencer sans tout recocher.
        if (this.modeGroupe || this.modeUtmFilter) {
            this.selectedRows = [];
        }
    }

    public doEdit(field) {
        this.tableGroupage = null;
        this.modeGroupe = false;
        this.editOriginals.clear();

        if (field.origin === "static") {
            this.fieldStatic = this.draftOf(field);
        } else {
            this.fields = [this.draftOf(field)];
        }
    }

    doEditGroup(tableDest: string, groupe: number): void {
        const fieldsTable = this.fieldsTable.filter(
            (f) => f.tableDest === tableDest && f.groupe === groupe
        );
        this.editOriginals.clear();
        this.fields = fieldsTable.map((f) => this.draftOf(f));
        this.tableGroupage = tableDest;
        this.modeGroupe = true;
    }

    /** Copie de travail d'un mapping pour une modale d'édition (cf. `editOriginals`). */
    private draftOf(original: TargetFieldDestination): TargetFieldDestination {
        const draft = JSON.parse(JSON.stringify(original));
        this.editOriginals.set(draft, original);
        return draft;
    }

    save(next = false) {
        if(next === true && !this.isValid){
            return;
        }
        const addFields: AddField[] = [];
        const loadFields: SqlLoadConf[] = [];
        const transforms: ConvertTypeTransformConf[] = [];

        // Traiter les champs existants dans fieldsTable
        this.fieldsTable.forEach((f) => {
            f = TargetFieldDestination.__init(f);
            if (f.origin === "static") {
                addFields.push(f.getAddField(this.tenant));
            } else {
                const sqlConf: SqlLoadConf = f.getSqlConf(this.tenant);
                loadFields.push(sqlConf);
                transforms.push(...f.getTransfom(sqlConf));
            }
        });

        // Ajouter les champs adminOnly comme champs statiques
        if (!this.isAuthorisedAdmin && this.adminOnlyFields && this.adminOnlyFields.length > 0) {
            this.adminOnlyFields.forEach((field) => {
                const alreadyExists = this.fieldsTable.some(
                    (ft) => ft.fieldDest === field.code && ft.tableDest === field.table?.code
                );

                if (!alreadyExists) {
                    let defaultValue = this.getDefaultValueForOnlyAdminField(field);

                    const staticField = new TargetFieldDestination();
                    staticField.origin = 'static';
                    staticField.tableDest = field.table?.code;
                    staticField.fieldDest = field.code;
                    staticField.meta = field;
                    staticField.value = defaultValue;
                    staticField.groupe = 0;
                    staticField.filter = { value: null };

                    addFields.push(staticField.getAddField(this.tenant));
                }
            });
        }

        const isComplet: boolean = this.etl.complet && this.isValid;
        this.completChange.emit(isComplet);

        const etlDesignDto: EtlDesignDTO = {
            id: this.id,
            load: {
                typeLoad: "SqlProcessingLoadConf",
                destTable: this.tables.map((t) => t.code),
                loads: loadFields,
                contactType: this.contactType
            } as SqlProcessingLoadConf,
            transforms,
            addFields,
            scriptPostMapping: this.scriptPostMapping,
            complet: isComplet
        };

        this.configuratorEtlService
            .updateDesignUsingPOST(this.tenant, etlDesignDto)
            .subscribe((data) => {
                if (next) {
                    this.next();
                } else {
                    this.messageService.add({
                        severity: "success",
                        summary: "Enregistré",
                        key: "popin",
                    });
                }
            });
    }

    next() {
        this.nextStep.emit({ uuid: this.id });
    }

    /* Mappings de plusieurs colonnes cochées */

    /** Lignes multiples : choix de la table, puis modale de mapping des colonnes cochées. */
    startMultiRows(): void {
        if (this.selectedRows.length > 0) {
            this.displayTableGroupage = true;
        }
    }

    /** Retour de la modale de choix de table à lignes multiples. */
    onTableGroupageChosen(table: string): void {
        this.displayTableGroupage = false;
        if (table) {
            this.multiRowsTable = table;
            this.mapSelectionAsRow(table);
        }
    }

    /** Numéro de la prochaine ligne d'une table à lignes multiples. */
    nextRowNumber(table: string): number {
        const rows = this.fieldsTable.filter((f) => f.tableDest === table).map((f) => f.groupe || 0);
        return (rows.length > 0 ? Math.max(...rows) : 0) + 1;
    }

    /** Les colonnes cochées forment une nouvelle ligne de la table. */
    mapSelectionAsRow(table: string): void {
        if (!table || this.selectedRows.length === 0) {
            return;
        }
        const index = this.nextRowNumber(table);
        this.modeGroupe = true;
        this.tableGroupage = table;
        this.fields = this.selectedRows.map((row) => {
            const target = new TargetFieldDestination();
            target.updateWithSourceField(row.source, index);
            return target;
        });
    }

    /** UTM : choix de la table (personal ou command), puis modale limitée à ses champs UTM. */
    startUtm(): void {
        if (this.selectedRows.length > 0) {
            this.openUtmFilterConfig();
        }
    }

    /**
     * Fermeture de la modale de filtre UTM par la croix : elle doit annuler comme le bouton
     * « Annuler ». `applyUtmFilterConfig()` ferme déjà la modale, donc `(onHide)` se déclenche
     * aussi après une validation — d'où le drapeau, sans lequel on annulerait le choix qui vient
     * d'être validé.
     */
    onHideUtmFilterConfig() {
        if (!this.utmFilterConfigClosedByAction) {
            this.applyUtmFilterConfig(null);
        }
        this.utmFilterConfigClosedByAction = false;
    }

    openUtmFilterConfig() {
        this.utmFilterDraft = this.utmFilterTable;
        this.displayUtmFilterConfig = true;
    }

    applyUtmFilterConfig(tableCode: string | null) {
        this.utmFilterConfigClosedByAction = true;
        this.displayUtmFilterConfig = false;
        if (!tableCode) {
            this.modeUtmFilter = false;
            this.utmFilterTable = null;
            return;
        }
        this.modeUtmFilter = true;
        this.utmFilterTable = tableCode;
        this.fields = this.selectedRows.map((row) => {
            const target = new TargetFieldDestination();
            target.updateWithSourceField(row.source, 0);
            target.tableDest = tableCode;
            target.utmMatching = true;
            return target;
        });
    }

    get utmFilterTables(): SelectItem[] {
        return (this.tables || [])
            .filter((t) => ['personal', 'command'].includes((t.code || '').toLowerCase()))
            .map((t) => ({ label: t.label || t.code, value: t.code }));
    }

    get effectiveOnlyTable(): string {
        if (this.modeUtmFilter && this.utmFilterTable) {
            return this.utmFilterTable;
        }
        return this.tableGroupage;
    }

    private isUtmFieldCode(code: string): boolean {
        const c = (code || '').toLowerCase();
        return ['provider_source', 'provider_medium', 'provider_campaign', 'provider_term', 'provider_content'].includes(c);
    }

    /**
     * Détermine si un champ doit être inclus dans la liste des champs disponibles pour le mapping
     * @param field Le champ à évaluer
     * @returns true si le champ doit être affiché, false sinon
     */
    private shouldIncludeField(field: FieldTable): boolean {
        let res = false;
        const excludeBasicConditions =
            field.pk ||
            field.fk ||
            field.autoIncrement ||
            field.virtualField === true ||
            field.categoryField === "TECHNICAL";

        if (excludeBasicConditions) {
            return false;
        }
        if (this.isAuthorisedAdmin) {
            return true;
        }
        return field.adminOnly !== true;
    }

    /**
     * Détermine si un champ doit être inclus dans la liste des champs obligatoires
     * (ceux qui doivent impérativement être mappés pour valider le formulaire)
     * @param f Le champ à évaluer
     * @returns true si le champ doit être considéré comme obligatoire, false sinon
     */
    private shouldIncludeInRequiredFields(f: FieldTable): boolean {

        // Vérification de l'obligation (notNull)
        // Un champ est obligatoire sauf s'il est adminOnly et que l'utilisateur n'est pas admin
        // (car dans ce cas, le champ sera auto-rempli avec une valeur par défaut)
        let notNull = false;
        if (f.notNull === true) {
            notNull = !(this.isAuthorisedAdmin === false && f.adminOnly === true);
        }

        // "ID de l'affilié" (catégorie AFFILIATION) est toujours obligatoire,
        // indépendamment du flag notNull configuré côté tenant
        const isAffiliation = f.categoryField === "AFFILIATION";

        return (notNull || isAffiliation) &&
            (f.pk === false || f.pk === undefined) &&
            (f.fk === false || f.fk == undefined) &&
            f.categoryField !== "TECHNICAL"
    }

    /**
     * Extrait tous les champs adminOnly qui ne doivent pas être affichés
     */
    private extractAdminOnlyFields(): void {
        this.adminOnlyFields = [];

        if (!this.isAuthorisedAdmin) {
            this.tables.forEach((table) => {
                table.fields.forEach((field) => {
                    if (field.adminOnly) {
                        const fieldWithTable = { ...field, table: table };
                        this.adminOnlyFields.push(fieldWithTable);
                    }
                });
            });
        }
    }

    /**
     * Retourne la valeur par défaut pour un champ adminOnly
     * @param field Le champ pour lequel obtenir la valeur par défaut
     */
    private getDefaultValueForOnlyAdminField(field: any): string {
        const tableName = field.table?.code;
        const fieldCode = field.code;

        if (tableName === 'command' && fieldCode === 'affiliate_id') {
            return this.partner;
        }
        switch (field.type) {
            case 'STRING':
            case 'TEXT':
                return '';
            case 'INTEGER':
            case 'LONG':
            case 'DOUBLE':
            case 'FLOAT':
                return '0';
            case 'BOOLEAN':
                return 'false';
            case 'DATE':
            case 'DATETIME':
            case 'TIMESTAMP':
                return new Date().toISOString();
            default:
                return null;
        }
    }
}

/** Une ligne de l'écran : une colonne du fichier et ses destinations. */
interface SourceRow {
    code: string;
    source: EtlFileSourceInterface;
    mappings: TargetFieldDestination[];
    /** Colonne mappée mais absente du fichier analysé. */
    missing: boolean;
}

type RowFilter = "all" | "unmapped" | "mapped" | "multiRows" | "utm";

/**
 * Filtres du tableau, dans l'ordre du choix segmenté. « Lignes multiples » et « UTM » reprennent les
 * critères des repères affichés à côté d'une destination (« ligne n° N », « UTM ») : une colonne y figure
 * dès qu'une de ses destinations porte le repère.
 */
const ROW_FILTERS: Record<RowFilter, { label: string; matches: (row: SourceRow) => boolean }> = {
    all: {label: "etl.new.design.rows-all", matches: () => true},
    unmapped: {label: "etl.new.design.rows-unmapped", matches: (row) => row.mappings.length === 0},
    mapped: {label: "etl.new.design.rows-mapped", matches: (row) => row.mappings.length > 0},
    multiRows: {label: "etl.new.design.rows-multi-rows", matches: (row) => row.mappings.some((m) => m.groupe > 0)},
    utm: {label: "etl.new.design.rows-utm", matches: (row) => row.mappings.some((m) => m.utmMatching)},
};

/** Champ obligatoire non mappé. */
interface RequiredField {
    tableDest: string;
    field: FieldTable;
    /** Champ d'une table à lignes multiples : il se mappe en cochant des colonnes, pas depuis le bandeau. */
    multiRows: boolean;
}

interface TargetField extends ColFileStructure {
    destinations?: Array<ITargetFieldDestination>;
}
